import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, WifiOff, Headphones, Moon, Share as ShareIcon } from 'lucide-react';
import { Button } from './ui/button';

// LocalStorage keys
const K_OPEN_COUNT = 'pwa_open_count';
const K_LAST_OPEN = 'pwa_last_open_ts';
const K_DISMISSED_AT = 'pwa_install_dismissed_at';
const K_DISMISSED_PERM = 'pwa_install_dismissed_permanent';

const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7d
const MIN_OPENS_BEFORE_PROMPT = 3;
// Treat opens in the same 60-min window as the same session so we don't
// count a single afternoon of navigating as 20 "opens".
const OPEN_COUNT_WINDOW_MS = 60 * 60 * 1000;

// Hide on auth flows + immersive player where we don't want to interrupt.
const HIDDEN_PREFIXES = [
  '/login',
  '/admin-login',
  '/writer-login',
  '/forgot-password',
  '/payment/callback',
  '/player/',
  '/reader/',
];

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari
  if (window.navigator?.standalone === true) return true;
  return false;
};

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const location = useLocation();

  const hiddenByRoute = useMemo(
    () => HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p)),
    [location.pathname]
  );

  // ── 1. Count sessions ─────────────────────────────────────────────────────
  // We consider a "session" a first open (or return after > 1h). Navigating
  // between routes doesn't bump the counter — otherwise every power user
  // would see the banner immediately.
  useEffect(() => {
    try {
      if (isStandalone()) return; // already installed
      const now = Date.now();
      const last = parseInt(localStorage.getItem(K_LAST_OPEN) || '0', 10);
      if (!last || now - last > OPEN_COUNT_WINDOW_MS) {
        const prev = parseInt(localStorage.getItem(K_OPEN_COUNT) || '0', 10);
        localStorage.setItem(K_OPEN_COUNT, String(prev + 1));
      }
      localStorage.setItem(K_LAST_OPEN, String(now));
    } catch { /* quota — ignore */ }
  }, []);

  // ── 2. Listen for beforeinstallprompt (Chrome/Edge/Android) ───────────────
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Clear state once app is actually installed.
    const installedHandler = () => {
      setDeferredPrompt(null);
      setVisible(false);
      setShowIosHelp(false);
      try { localStorage.setItem(K_DISMISSED_PERM, '1'); } catch { /* ignore */ }
    };
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // ── 3. Decide whether to show ─────────────────────────────────────────────
  useEffect(() => {
    if (isStandalone()) { setVisible(false); return; }
    let perm = null, dismissedAt = 0, opens = 0;
    try {
      perm = localStorage.getItem(K_DISMISSED_PERM);
      dismissedAt = parseInt(localStorage.getItem(K_DISMISSED_AT) || '0', 10);
      opens = parseInt(localStorage.getItem(K_OPEN_COUNT) || '0', 10);
    } catch { /* ignore */ }
    if (perm === '1') { setVisible(false); return; }
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) {
      setVisible(false); return;
    }
    if (opens < MIN_OPENS_BEFORE_PROMPT) { setVisible(false); return; }

    // Chrome/Android: need deferredPrompt to proceed.
    // iOS: no beforeinstallprompt event, fall back to the "Add to Home Screen"
    // help modal so users actually learn how to install.
    if (deferredPrompt || isIos()) {
      // Debounce — give the page a breath before we pop the banner so it
      // doesn't fight layout on first paint.
      const id = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [deferredPrompt, location.pathname]);

  const handleInstallClick = async () => {
    // iOS: no programmatic install — show manual A2HS instructions.
    if (!deferredPrompt && isIos()) {
      setShowIosHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        try { localStorage.setItem(K_DISMISSED_PERM, '1'); } catch { /* ignore */ }
      } else {
        // Dismissed via native — treat as cooldown, not permanent.
        try { localStorage.setItem(K_DISMISSED_AT, String(Date.now())); } catch { /* ignore */ }
      }
    } catch { /* user cancelled */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    setVisible(false);
    try { localStorage.setItem(K_DISMISSED_AT, String(Date.now())); } catch { /* ignore */ }
  };

  const handleNeverAgain = () => {
    setVisible(false);
    setShowIosHelp(false);
    try { localStorage.setItem(K_DISMISSED_PERM, '1'); } catch { /* ignore */ }
  };

  if (hiddenByRoute) return null;

  // iOS help modal — shown when user taps Install on iOS (no programmatic
  // install available). Walks them through Share → Add to Home Screen.
  if (showIosHelp) {
    return (
      <div
        className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
        onClick={() => setShowIosHelp(false)}
        data-testid="pwa-ios-help-modal"
      >
        <div
          className="w-full max-w-md bg-slate-900 border border-indigo-500/40 rounded-2xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 bg-gradient-to-br from-indigo-700 to-purple-700">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.2em] text-indigo-200 font-semibold">
                Install on iPhone
              </p>
              <button
                type="button"
                onClick={() => setShowIosHelp(false)}
                className="text-white/80 hover:text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="text-xl font-bold text-white mt-2">
              Add Smarter Learning Hub to your Home Screen
            </h3>
          </div>

          <ol className="p-5 space-y-3 text-sm text-gray-200">
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">1</span>
              <span>
                Tap the <ShareIcon className="inline w-4 h-4 mx-1 text-indigo-300" />
                <strong className="text-white">Share</strong> icon in Safari's bottom bar.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">2</span>
              <span>Scroll and tap <strong className="text-white">Add to Home Screen</strong>.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">3</span>
              <span>Tap <strong className="text-white">Add</strong> — the app launches full-screen, no browser bars.</span>
            </li>
          </ol>

          <div className="px-5 pb-5 flex gap-2">
            <button
              type="button"
              onClick={handleNeverAgain}
              className="flex-1 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-slate-800 transition-colors"
              data-testid="pwa-never-again"
            >
              Don't show again
            </button>
            <Button
              onClick={() => setShowIosHelp(false)}
              className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid="pwa-ios-got-it"
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!visible) return null;

  const onIos = isIos();

  return (
    <div
      className="fixed left-3 right-3 sm:left-auto sm:right-4 sm:w-96 z-[45] animate-in fade-in slide-in-from-bottom-4 duration-300"
      // Sit above the mini-player on mobile (which lives ~64px + safe area
      // from the bottom when the bottom nav is visible). Using a generous
      // offset so the two cards don't overlap even when both show.
      style={{
        bottom: 'calc(144px + env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="pwa-install-banner"
    >
      <div className="relative rounded-2xl bg-gradient-to-br from-indigo-700 via-purple-700 to-fuchsia-700 text-white shadow-2xl shadow-indigo-950/50 overflow-hidden border border-indigo-400/40">
        {/* Shimmer backdrop */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.35), transparent 60%), radial-gradient(circle at 90% 90%, rgba(255,255,255,0.15), transparent 55%)',
          }}
        />

        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
          aria-label="Dismiss install prompt"
          data-testid="pwa-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="bg-white/15 backdrop-blur-sm p-2.5 rounded-xl flex-shrink-0 ring-1 ring-white/20">
              <Download className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0 pr-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-200 font-semibold">
                Install the App
              </p>
              <h3 className="font-bold text-base sm:text-lg leading-tight">
                Smarter Learning Hub on your home screen
              </h3>
            </div>
          </div>

          {/* Perk highlights — wrap so none spill on tiny phones */}
          <ul className="mt-3 grid grid-cols-1 gap-1.5 text-[12px] text-indigo-100">
            <li className="flex items-center gap-2">
              <Headphones className="w-3.5 h-3.5 text-indigo-200 flex-shrink-0" />
              Lock-screen audio controls while you listen
            </li>
            <li className="flex items-center gap-2">
              <WifiOff className="w-3.5 h-3.5 text-indigo-200 flex-shrink-0" />
              Offline reading for downloaded books
            </li>
            <li className="flex items-center gap-2">
              <Moon className="w-3.5 h-3.5 text-indigo-200 flex-shrink-0" />
              Sleep Timer that keeps playing in the background
            </li>
          </ul>

          <div className="mt-4 flex items-center gap-2">
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-white text-indigo-700 hover:bg-gray-100 font-semibold"
              size="sm"
              data-testid="pwa-install-button"
            >
              <Download className="w-4 h-4 mr-2" />
              {onIos ? 'Show me how' : 'Install now'}
            </Button>
            <button
              type="button"
              onClick={handleNeverAgain}
              className="text-[11px] text-indigo-200 hover:text-white px-2 py-2 transition-colors flex-shrink-0"
              data-testid="pwa-never-again-inline"
            >
              Don't ask again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
